import { useState } from 'react';

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const fmt = (n) =>
  `GHS ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * PaymentLinkModal
 * Props:
 *   sale        — { id, balanceDue, customer: { email, phone } }
 *   onClose     — called when modal is dismissed
 */
export default function PaymentLinkModal({ sale, onClose }) {
  const [method, setMethod]       = useState('momo');
  const [email, setEmail]         = useState(sale.customer?.email || '');
  const [generating, setGenerating] = useState(false);
  const [link, setLink]           = useState('');
  const [error, setError]         = useState('');
  const [copied, setCopied]       = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const tok = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` };

  const handleGenerate = async () => {
    setGenerating(true); setError('');
    try {
      const r = await fetch(`${API}/payments/balance-link`, {
        method: 'POST', headers,
        body: JSON.stringify({ saleId: sale.id, paymentMethod: method, customerEmail: email || undefined }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Failed to generate link'); return; }
      setLink(data.authorization_url);
    } catch {
      setError('Network error');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSendEmail = async () => {
    if (!email.trim()) return;
    setSendingEmail(true);
    try {
      // Send a simple email with the payment link
      const r = await fetch(`${API}/payments/balance-link/email`, {
        method: 'POST', headers,
        body: JSON.stringify({ saleId: sale.id, email: email.trim(), paymentUrl: link }),
      });
      if (r.ok) setEmailSent(true);
      else setError('Failed to send email');
    } catch {
      setError('Network error');
    } finally {
      setSendingEmail(false);
    }
  };

  const receiptId = `#${String(sale.id).padStart(4, '0')}`;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 24px 60px rgba(15,23,42,0.2)', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ background: '#1B3A6B', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>Request Payment</div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', marginTop: 2 }}>
              Sale {receiptId} · Balance due: <strong style={{ color: '#D4A017' }}>{fmt(sale.balanceDue)}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', color: '#DC2626', fontSize: '0.82rem', marginBottom: 16 }}>
              {error}
            </div>
          )}

          {!link ? (
            <>
              {/* Payment method */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>
                  Payment Method
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { key: 'momo', label: '📱 MoMo' },
                    { key: 'card', label: '💳 Card' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setMethod(key)}
                      style={{
                        flex: 1, padding: '10px 0', borderRadius: 10,
                        border: method === key ? '2px solid #1B3A6B' : '2px solid #E2E8F0',
                        background: method === key ? 'rgba(27,58,107,0.06)' : 'white',
                        fontWeight: method === key ? 700 : 400,
                        color: method === key ? '#1B3A6B' : '#64748B',
                        cursor: 'pointer', fontSize: '0.9rem',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer email */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>
                  Customer Email (optional)
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@email.com"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
                />
                <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: 4 }}>Used by Paystack for the payment confirmation</div>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: generating ? '#94A3B8' : '#1B3A6B', color: 'white', fontWeight: 700, fontSize: '0.92rem', cursor: generating ? 'default' : 'pointer' }}
              >
                {generating ? 'Generating link…' : `Generate ${fmt(sale.balanceDue)} payment link`}
              </button>
            </>
          ) : (
            <>
              {/* Success state — link generated */}
              <div style={{ background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16A34A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  ✓ Payment link ready
                </div>
                <div style={{ wordBreak: 'break-all', fontSize: '0.78rem', color: '#1B3A6B', fontFamily: 'monospace', lineHeight: 1.5 }}>
                  {link}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button
                  onClick={handleCopy}
                  style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1.5px solid #E2E8F0', background: copied ? 'rgba(22,163,74,0.08)' : 'white', color: copied ? '#16A34A' : '#1B3A6B', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  {copied ? '✓ Copied!' : '📋 Copy link'}
                </button>
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#1B3A6B', color: 'white', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  ↗ Open
                </a>
              </div>

              {/* Send by email */}
              {emailSent ? (
                <div style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)', borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', color: '#16A34A', fontWeight: 600, textAlign: 'center' }}>
                  ✓ Payment link emailed to {email}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Send link to email…"
                    style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: '0.82rem', outline: 'none' }}
                  />
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !email.trim()}
                    style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1B3A6B', color: 'white', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', opacity: sendingEmail || !email.trim() ? 0.6 : 1 }}
                  >
                    {sendingEmail ? '…' : '✉ Send'}
                  </button>
                </div>
              )}

              <div style={{ marginTop: 12, padding: '10px 14px', background: '#FFFBEB', borderRadius: 8, fontSize: '0.75rem', color: '#92400E' }}>
                Share this link with the customer. It expires in 1 hour. Once paid, the balance will be automatically updated.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
